import * as ArtistServices from 'widget/ArtistRadio/services';
import addFit from 'widget/utils/helpers/addFit';
import Analytics from 'modules/Analytics/Analytics';
import BigThumbnail from 'widget/components/BigThumbnail';
import END_REASON from 'modules/Analytics/constants/endReason';
import ExternalLink from 'widget/components/ExternalLink';
import InfoCard from 'widget/components/InfoCard';
import LeftButtonGroup from 'widget/components/LeftButtonGroup';
import LeftButtonGroupControls from 'widget/components/LeftButtonGroupControls';
import mediaQueries from 'styles/mediaQueries';
import PlaybackError from 'widget/Errors/PlaybackError';
import PlayButton from 'widget/components/PlayButton';
import PlayButtonWrapper from 'widget/components/PlayButtonWrapper';
import Player from 'widget/contexts/Player';
import player from 'widget/player';
import PlayerContainer from 'widget/components/PlayerContainer';
import PlayerState, {
  PlayerErrorState,
} from 'modules/Player/constants/playerState';
import PlayNextButton from 'widget/components/PlayNextButton';
import Progress from 'widget/components/Progress';
import RightButtonGroup from 'widget/components/RightButtonGroup';
import ShareButton from 'widget/components/ShareButton';
import ShareCard from 'widget/components/ShareCard';
import ShouldShow from 'components/ShouldShow';
import Toolbar from 'widget/components/Toolbar';
import unescapeHTMLEntities from 'decode-html';
import useModal from 'widget/components/Modal/hooks/useModal';
import useMount from 'hooks/useMount';
import useTrackers from 'widget/contexts/Trackers/hooks/useTrackers';
import withPlayerJS from 'widget/components/withPlayerJS';
import { Artist } from 'widget/ArtistRadio/types';
import { Events } from 'widget/trackers/types';
import {
  generateArtistRadioTrackingData,
  getArtistRadioAnalyticsPayload,
} from 'widget/ArtistRadio/helpers/analytics';
import { GetPlaybackErrorMessage } from 'widget/Errors/PlaybackError/helpers/getErrorMessageTranslator';
import { PlayerJSRefs } from 'widget/components/withPlayerJS/types';
import { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { TransportInterface } from 'widget/utils/transport/types';
import { usePlayerProgress, useTranslate } from 'widget/hooks';
import { WIDGET_DIMENSIONS } from 'constants/widgetDimensions';
import { withRetries } from 'widget/utils/helpers/withRetries';

function ArtistRadioPlayer({
  ampUrl,
  analytics,
  analyticsData,
  artistRadioUrl,
  autoplay,
  customRadioEnabled,
  embedUrl,
  followUrl,
  getPlaybackErrorMessage,
  hasPlayed,
  imageUrl,
  isError,
  profileId,
  positionBottom,
  refs,
  sessionId,
  shareText,
  shareTitle,
  skipDisabled = true,
  stationArtist,
  transport,
  websiteUrl,
}: {
  ampUrl: string;
  analytics?: Analytics;
  analyticsData: {
    name: string;
    subName?: string;
    playedFrom: number;
    subId?: string;
    subType?: string;
  };
  artistRadioUrl: string;
  autoplay?: boolean;
  customRadioEnabled?: boolean;
  embedUrl: string;
  followUrl: string;
  getPlaybackErrorMessage: GetPlaybackErrorMessage;
  hasPlayed: boolean;
  imageUrl?: string;
  isError?: boolean;
  profileId?: string;
  positionBottom: boolean;
  refs?: PlayerJSRefs;
  sessionId?: string;
  shareText: string;
  shareTitle: string;
  skipDisabled?: boolean;
  stationArtist: Artist;
  transport: TransportInterface;
  websiteUrl: string;
}) {
  const { playerState, playNextArtistRadio, playArtistStation } = useContext(
    Player.Context,
  );
  const { artistId } = stationArtist;
  const [playClicked, setPlayClicked] = useState(false);
  const [streamId, setStreamId] = useState('');
  const [isAA, setIsAA] = useState(false);
  const [isPlaybackError, setIsPlaybackError] = useState<boolean>(false);
  const [streamInitTime, setStreamInitTime] = useState<number>(0);
  const trackingDataRef = useRef(
    generateArtistRadioTrackingData({ ...analyticsData, artistId }),
  );
  const playedFromRef = useRef(analyticsData.playedFrom);
  const [progress] = usePlayerProgress();
  const isPlaying = playerState === PlayerState.Playing;
  const isBuffering = playerState === PlayerState.Buffering;
  const isLoading = playerState === PlayerState.Loading;
  const translate = useTranslate();

  const trackerData = {
    name: analyticsData.name,
    type: 'artist',
    typeId: artistId,
  };

  const [ShareModal, toggleShareModal] = useModal(
    <ShareCard
      analytics={analytics}
      analyticsData={{
        id: artistId,
        name: trackerData.name,
        type: trackerData.type,
        subType: 'album',
        subId: analyticsData.subId,
        subName: analyticsData.subName,
      }}
      dimensions={WIDGET_DIMENSIONS.ARTIST}
      followUrl={followUrl}
      shareText={shareText}
      shareTitle={shareTitle}
      url={artistRadioUrl}
      urlWithQuery={embedUrl}
      websiteUrl={websiteUrl}
    />,
  );

  const [UnsupportedModal, toggleUnsupportedModal] = useModal(
    <InfoCard
      body={unescapeHTMLEntities(
        `<p>
          ${translate(
            "We're sorry! This station is not available in your country.",
          )}
        </p>`,
      )}
      title={translate('Station Not Available')}
      url=""
    />,
  );

  const trackers = useTrackers();

  const play = async (
    _e?: SyntheticEvent,
    playedFrom = playedFromRef.current,
  ) => {
    if (customRadioEnabled) {
      if (playedFrom !== playedFromRef.current) {
        playedFromRef.current = playedFrom;
        trackingDataRef.current = generateArtistRadioTrackingData({
          ...analyticsData,
          artistId,
          playedFrom,
        });
      }

      if (isPlaying) {
        trackers.track(Events.Pause);
        analytics?.track(Events.Pause, trackingDataRef.current);
        analytics?.track(
          Events.StreamEnd,
          getArtistRadioAnalyticsPayload(trackingDataRef.current, {
            endReason: END_REASON.STOP,
            listenTime: Math.round(progress?.position ?? 0),
          }),
        );
      } else {
        analytics?.track(Events.Play, trackingDataRef.current);
        setStreamInitTime(Date.now());
        trackers.track(Events.Play, trackerData);
      }

      setPlayClicked(true);

      const stream = await ArtistServices.fetchArtistStream({
        ampUrl,
        artistId,
        profileId,
        sessionId,
        transport,
      });
      setStreamId(stream.id);
      playArtistStation(stream.id);
    } else {
      toggleUnsupportedModal();
    }
  };

  /**
   * IHRWEB-14939 methods mute, setCurrentTime, setLoop, setVolume, & unmute
   * are currently only externally triggered via player.js; react event
   * parameter included for methods with args for possible widget UI.
   * analytics tbd in IHRWEB-14917.
   */
  const mute = () => {
    player.mute();
    trackers.track(Events.Mute);
  };

  const setLoop = (_e: null | SyntheticEvent, shouldLoop: string | boolean) => {
    if (
      typeof shouldLoop === 'boolean' ||
      shouldLoop === 'true' ||
      shouldLoop === 'false'
    ) {
      player.setLoop(shouldLoop && shouldLoop !== 'false');
    }
  };

  const setVolume = (_e: null | SyntheticEvent, volume: string | number) => {
    player.setVolume(parseInt(volume as string, 10));
  };

  const unmute = () => {
    player.unmute();
    trackers.track(Events.Unmute, { isPlaying });
  };

  if (refs) {
    /* eslint-disable no-param-reassign */
    refs.mute.current = mute;
    refs.pause.current = play; // pause maps to toggling play method
    refs.play.current = play;
    refs.setLoop.current = setLoop;
    refs.setVolume.current = setVolume;
    refs.unmute.current = unmute;
    /* eslint-enable no-param-reassign */
  }

  useMount(() => {
    setIsAA(
      window?.analyticsData?.global?.user?.subscriptionTier === 'PREMIUM',
    );
    if (autoplay) play();
  });

  const playNextArtistRadioWithRetries = withRetries(
    () => playNextArtistRadio(streamId, false),
    () => setIsPlaybackError(true),
  );

  useEffect(() => {
    if (playerState === PlayerState.Complete) {
      playNextArtistRadioWithRetries();
    }
  }, [playerState]);

  useEffect(() => {
    if (playerState === PlayerState.Playing) {
      setIsPlaybackError(false);
      if (streamInitTime) {
        analytics?.track(
          Events.StreamStart,
          getArtistRadioAnalyticsPayload(trackingDataRef.current, {
            playbackStartTime: Date.now(),
            streamInitTime,
          }),
        );
        setStreamInitTime(0);
        trackers.track(Events.StreamStart, trackerData);
      }
    }
  }, [playerState, streamInitTime]);

  useEffect(() => {
    if (
      isError ||
      Object.values(PlayerErrorState).includes(playerState as PlayerErrorState)
    ) {
      const networkError = [
        PlayerErrorState.AccessDenied,
        PlayerErrorState.NetworkError,
      ].includes(playerState as PlayerErrorState);
      analytics?.track(
        Events.PlaybackError,
        getArtistRadioAnalyticsPayload(trackingDataRef.current, {
          errorMessage: getPlaybackErrorMessage(
            playerState as PlayerErrorState,
          ),
          errorType: networkError ? 'network_error' : 'playback_failure',
        }),
      );
    }
  }, [isError, playerState]);

  if (isError && isPlaybackError) {
    return <PlaybackError error={playerState as PlayerErrorState} />;
  }

  return (
    <div>
      <PlayerContainer positionBottom={positionBottom}>
        <Progress scrubDisabled={!isAA} supportMobileView={false} />
        <Toolbar>
          <LeftButtonGroup>
            <PlayButtonWrapper data-test="play-button-wrapper">
              <PlayButton
                isLoading={isBuffering || isLoading}
                isPlaying={isPlaying || isBuffering || isLoading}
                onClick={play}
              />
            </PlayButtonWrapper>
            <LeftButtonGroupControls>
              <ShouldShow
                shouldShow={(hasPlayed || playClicked) && !skipDisabled}
              >
                <PlayNextButton
                  onClick={() => {
                    analytics?.track(
                      Events.Skip,
                      generateArtistRadioTrackingData({
                        ...analyticsData,
                        artistId,
                      }),
                    );
                    return playNextArtistRadioWithRetries();
                  }}
                  sizeToHide={mediaQueries.max.width['149']}
                />
              </ShouldShow>
            </LeftButtonGroupControls>
          </LeftButtonGroup>
          <RightButtonGroup>
            <ShareButton
              onClick={toggleShareModal}
              sizeToHide={mediaQueries.max.width['199']}
            />
            {imageUrl && (
              <ExternalLink data-test="thumbnail-link" href={artistRadioUrl}>
                <BigThumbnail
                  alt={`${shareTitle} Radio`}
                  src={addFit(imageUrl)}
                />
              </ExternalLink>
            )}
          </RightButtonGroup>
        </Toolbar>
      </PlayerContainer>
      {ShareModal}
      {UnsupportedModal}
    </div>
  );
}

export default withPlayerJS(ArtistRadioPlayer);
